import {
    ENGAGEMENT_TEAM_ROLES,
    type EngagementTeamRole,
    TaskTemplateAssigneeRole,
} from "@common/types/taskTemplateAssigneeRole";
import type { UserBase } from "@common/types/user";
import type { WorkspaceTimezone } from "@common/workspaceTimezone";
import type { WorkspaceAvatarGroupItem } from "@components/avatar/WorkspaceAvatarGroup";
import type {
    DataGridCellTooltip,
    DataGridPeopleMember,
} from "@design-system/DataGrid";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import {
    ASSIGNEE_ROLE_LABEL,
    ASSIGNEE_ROLE_OPTIONS,
} from "../closeTemplateTasksDrawer.constants";
import { formatCloseTemplateDateRange } from "../formatCloseTemplateDateRange";
import { PERIOD_LABELS } from "../serviceTemplateSchedule.constants";
import {
    getServiceTemplateScheduleDisplayStatus,
    type ServiceTemplateScheduleDisplayStatus,
} from "../serviceTemplateScheduleDisplayStatus";
import type {
    ServiceTemplateScheduleEntityInfo,
    ServiceTemplateScheduleGroupRow,
    ServiceTemplateScheduleLeafRow,
    ServiceTemplateScheduleRow,
} from "../serviceTemplateSchedulesViewConfig";
import {
    EMPTY_TEMPLATE_ESTIMATE_STATS,
    getRoleEstimatedMinutes,
    type TemplateEstimateStats,
} from "../templateEstimateStats";

/** An organization member as the engagement team shows them. */
export type EngagementTeamMember = Pick<
    UserBase,
    "id" | "name" | "preferredName" | "avatar" | "iconColor" | "email"
>;

/** A client, with the zone its schedules' days are read in. */
export interface ScheduleListWorkspaceInfo {
    id: string;
    name: string;
    iconColor: string | null;
    timezone?: WorkspaceTimezone;
}

/** What the columns look up per row, built once from the page's queries. */
export interface ScheduleListLookups {
    templateNameById: ReadonlyMap<string, string>;
    /** Derived from template tasks; never stored on the schedule. */
    templateEstimateById: ReadonlyMap<string, TemplateEstimateStats>;
    /** Schedules never persist archived; their template does (PRO-10571). */
    archivedTemplateIds: ReadonlySet<string>;
    memberById: ReadonlyMap<number, EngagementTeamMember>;
    /** The org's entities: a schedule whose entity is missing reads as deleted. */
    entityInfoByUuid: ReadonlyMap<string, ServiceTemplateScheduleEntityInfo>;
    workspaceInfoByUuid: ReadonlyMap<string, ScheduleListWorkspaceInfo>;
}

type ScheduleRow = ServiceTemplateScheduleLeafRow;

interface RowLookups {
    row: ScheduleRow;
    lookups: ScheduleListLookups;
}

/** Group rows are summary rows, so only schedules have values. */
export const scheduleValue =
    <TValue>(read: (row: ScheduleRow) => TValue) =>
    (row: ServiceTemplateScheduleRow) =>
        row.rowType === "schedule" ? read(row) : undefined;

export const getScheduleDisplayStatus = ({ row, lookups }: RowLookups) =>
    getServiceTemplateScheduleDisplayStatus({
        status: row.status,
        isTemplateArchived: lookups.archivedTemplateIds.has(row.templateId),
        isClientDeleted: !lookups.entityInfoByUuid.has(row.entityUuid),
    });

/** Archived and deleted aren't obvious from the label, so they explain why on hover. */
const STATUS_REASONS: Partial<
    Record<ServiceTemplateScheduleDisplayStatus, string>
> = {
    archived:
        "This schedule is archived because its template has been archived.",
    deleted: "This schedule's client is no longer part of your organization.",
};

export const getStatusReason = (args: RowLookups) =>
    STATUS_REASONS[getScheduleDisplayStatus(args)];

export const getClientInfo = ({ row, lookups }: RowLookups) =>
    row.workspaceId === null
        ? undefined
        : lookups.workspaceInfoByUuid.get(row.workspaceId);

export const getNextServicePeriod = ({
    nextServiceStartDate,
    nextServiceEndDate,
}: ScheduleRow) =>
    nextServiceStartDate || nextServiceEndDate
        ? formatCloseTemplateDateRange(
              nextServiceStartDate ?? null,
              nextServiceEndDate ?? null,
          )
        : null;

/** Groups share their schedules' template and period, so these read any row. */
interface TemplateRowLookups {
    row: ServiceTemplateScheduleRow;
    lookups: ScheduleListLookups;
}

export const getPeriodLabel = (row: ServiceTemplateScheduleRow) =>
    PERIOD_LABELS[row.period];

const getEstimateStats = ({ row, lookups }: TemplateRowLookups) =>
    lookups.templateEstimateById.get(row.templateId) ??
    EMPTY_TEMPLATE_ESTIMATE_STATS;

export const getEstimatedTimeText = (args: TemplateRowLookups) =>
    formatDuration(getEstimateStats(args).totalEstimatedTime) || null;

/** Each role's share, when more than one role has time. */
export const getEstimatedTimeTooltip = (
    args: TemplateRowLookups,
): DataGridCellTooltip | undefined => {
    const stats = getEstimateStats(args);
    const roleRows = ASSIGNEE_ROLE_OPTIONS.flatMap((role) => {
        const minutes = getRoleEstimatedMinutes(stats, role);
        return minutes > 0
            ? [
                  {
                      label: ASSIGNEE_ROLE_LABEL[role],
                      value: formatDuration(minutes),
                  },
              ]
            : [];
    });
    return roleRows.length > 1 ? { sections: [roleRows] } : undefined;
};

const ROLE_TO_FIELD: Record<
    EngagementTeamRole,
    "preparerId" | "reviewerId" | "managerId"
> = {
    [TaskTemplateAssigneeRole.PREPARER]: "preparerId",
    [TaskTemplateAssigneeRole.REVIEWER]: "reviewerId",
    [TaskTemplateAssigneeRole.MANAGER]: "managerId",
};

interface TeamRole {
    role: EngagementTeamRole;
    minutes: number;
}

type TeamSlot =
    | { kind: "member"; member: EngagementTeamMember; roles: TeamRole[] }
    | { kind: "unassigned"; role: TeamRole };

const formatRoleEstimate = ({ role, minutes }: TeamRole) =>
    minutes > 0
        ? `${ASSIGNEE_ROLE_LABEL[role]} · ${formatDuration(minutes)}`
        : ASSIGNEE_ROLE_LABEL[role];

/** "Name · Preparer · 45m" for one role; the name over a row per role for several. */
const toMemberTooltip = ({
    name,
    roles,
}: {
    name: string;
    roles: readonly TeamRole[];
}): DataGridCellTooltip =>
    roles.length === 1
        ? `${name} · ${formatRoleEstimate(roles[0])}`
        : {
              title: name,
              sections: [
                  roles.map(({ role, minutes }) => ({
                      label: ASSIGNEE_ROLE_LABEL[role],
                      value: formatDuration(minutes),
                  })),
              ],
          };

const toPeopleMember = (slot: TeamSlot): DataGridPeopleMember => {
    if (slot.kind === "unassigned") {
        return {
            name: "Unassigned",
            roles: [ASSIGNEE_ROLE_LABEL[slot.role.role]],
            isPlaceholder: true,
            tooltip: `Unassigned · ${formatRoleEstimate(slot.role)}`,
        };
    }
    const name = slot.member.preferredName ?? slot.member.name;
    return {
        name,
        avatarUrl: slot.member.avatar ?? undefined,
        roles: slot.roles.map(({ role }) => ASSIGNEE_ROLE_LABEL[role]),
        tooltip: toMemberTooltip({ name, roles: slot.roles }),
    };
};

/**
 * One avatar per person in role order, with every role they hold, and a
 * placeholder for each role the template estimates time for that nobody fills.
 */
export const toEngagementTeam = (args: RowLookups): DataGridPeopleMember[] => {
    const { row, lookups } = args;
    const stats = getEstimateStats(args);
    const slots: TeamSlot[] = [];
    const rolesByMemberId = new Map<number, TeamRole[]>();
    for (const role of ENGAGEMENT_TEAM_ROLES) {
        const memberId = row[ROLE_TO_FIELD[role]];
        const member =
            memberId === null ? undefined : lookups.memberById.get(memberId);
        const teamRole = {
            role,
            minutes: getRoleEstimatedMinutes(stats, role),
        };
        const memberRoles = member && rolesByMemberId.get(member.id);
        if (memberRoles) {
            memberRoles.push(teamRole);
        } else if (member) {
            const roles = [teamRole];
            rolesByMemberId.set(member.id, roles);
            slots.push({ kind: "member", member, roles });
        } else if (teamRole.minutes > 0) {
            slots.push({ kind: "unassigned", role: teamRole });
        }
    }
    return slots.map(toPeopleMember);
};

interface GroupLookups {
    row: ServiceTemplateScheduleGroupRow;
    lookups: ScheduleListLookups;
}

/** The group's known clients, and how many distinct clients it spans, known or not. */
export const getGroupClients = ({ row, lookups }: GroupLookups) => {
    const workspaceIds = new Set(
        row.children.flatMap((child) =>
            child.workspaceId === null ? [] : [child.workspaceId],
        ),
    );
    const workspaces: WorkspaceAvatarGroupItem[] = [...workspaceIds].flatMap(
        (workspaceId) => {
            const workspace = lookups.workspaceInfoByUuid.get(workspaceId);
            return workspace ? [{ workspace }] : [];
        },
    );
    return { workspaces, clientCount: workspaceIds.size };
};

export const getGroupEntities = ({ row, lookups }: GroupLookups) =>
    row.children.flatMap((child) => {
        const entity = lookups.entityInfoByUuid.get(child.entityUuid);
        return entity ? [entity] : [];
    });
